import React, { useState, useEffect, useRef } from "react";

import { NotificationManager } from 'react-notifications';
import VendorHeader from "../component/VendorHeader";
import VendorFooter from "../component/VendorFooter";
import Sidebar from "../component/Sidebar";

import { API_BASE_URL, IMAGE_URL } from "../utils/app_url";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";



const VendorProductImport = () => {
    const { selectedLanguage, langJson } = useLanguage();
    const { user } = useAuth();
    const [csvColumns, setCsvColumns] = useState([]);
    const [mapping, setMapping] = useState({});
    const [profileSaved, setProfileSaved] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [imageZip, setImageZip] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [step, setStep] = useState(1);
    const [loadingDemo, setLoadingDemo] = useState(false);
    const [loadingMapping, setLoadingMapping] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);

    console.log("Language JSON:", langJson.importProducts); // Debugging line to check language data
    console.log("Selected Language:", selectedLanguage); // Debugging line to check selected language

    const SYSTEM_FIELDS = [
        { key: "article_no", label: langJson?.articleno || "Article No" },
        { key: "order_no", label: langJson?.orderno || "Order No" },
        { key: "title", label: langJson?.name || "Name" },
        { key: "brand", label: langJson?.brand || "Brand" },
        { key: "color", label: langJson?.color || "Color" },
        { key: "description", label: langJson?.description || "Description" },
        { key: "is_public", label: langJson?.public || "Is Public" },
        { key: "size", label: langJson?.size || "Size" },
        { key: "stock", label: langJson?.instock || "Stock" },
        { key: "currency", label: langJson?.currency || "Currency" },
        { key: "price", label: langJson?.price || "Price" },
        { key: "unit_price", label: langJson?.unitprice || "Unit Price" },
        { key: "sale_price", label: langJson?.originalprice || "Original Price" },
        { key: "season", label: langJson?.maincategorygroupseason || "Main Category/ Season" },
        { key: "pro_cat_id", label: langJson?.subcategory || "Sub Category" },
        { key: "cat_id", label: langJson?.showcategory || "Show Category" },
        { key: "made_in", label: langJson?.madein || "Made In" },
        { key: "image", label: langJson?.image || "Image" },
    ];
    // Error state removed, use NotificationManager for notifications

    // Step 1: Upload sample file (Excel only) to get columns
    const handleSampleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCsvFile(file);
        // ...existing code...
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext === 'xls' || ext === 'xlsx') {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const headers = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
                setCsvColumns(headers);
                setStep(2);
            };
            reader.readAsArrayBuffer(file);
        } else {
            NotificationManager.error(langJson?.unsupportedFileType || "Unsupported file type. Please upload an Excel file (.xls, .xlsx).", langJson?.error || "Error");
        }
    };

    // Step 2: Save mapping profile
    const saveProfile = async () => {
        setLoadingMapping(true);
        try {
            const res = await fetch(`${API_BASE_URL}products/profile`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
                body: JSON.stringify({ mapping }),
            });
            const data = await res.json();
            if (data.success) {
                setProfileSaved(true);
                setStep(3);
                NotificationManager.success(langJson?.mappingProfileSaved || "Mapping profile saved successfully.", langJson?.success || "Success");
            } else {
                NotificationManager.error(langJson?.failedToSaveProfile || "Failed to save profile", langJson?.error || "Error");
            }
        } catch {
            NotificationManager.error(langJson?.failedToSaveProfile || "Failed to save profile", langJson?.error || "Error");
        } finally {
            setLoadingMapping(false);
        }
    };

    // Fetch mapping profile (on mount or as needed)
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}products/profile`, {
                    headers: {
                        Authorization: `Bearer ${user?.authorisation?.token}`,
                    },
                });
                const data = await res.json();
                if (data.mapping) {
                    setMapping(data.mapping);
                    setProfileSaved(true);
                }
            } catch { }
        };
        fetchProfile();
    }, [user]);

    // Step 2: Handle mapping change
    const handleMappingChange = (sysKey, csvCol) => {
        setMapping((prev) => {
            // Remove the csvCol from any other system field to prevent duplicate mapping
            const updated = { ...prev };
            Object.keys(updated).forEach((key) => {
                if (key !== sysKey && updated[key] === csvCol) {
                    updated[key] = "";
                }
            });
            updated[sysKey] = csvCol;
            return updated;
        });
    };

    // Step 3: Upload Excel and images
    const handleCsvUpload = (e) => {
        setCsvFile(e.target.files[0]);
    };
    const handleImageZip = (e) => {
        setImageZip(e.target.files[0]);
    };

    const handleImport = async () => {
        setLoadingImport(true);
        // Validation for Excel and images upload
        if (!csvFile) {
            NotificationManager.error(langJson?.excelRequired || "Please select an Excel file before importing.", langJson?.excelRequired || "Excel Required");
            setLoadingImport(false);
            return;
        }
        const excelExt = csvFile.name.split('.').pop().toLowerCase();
        if (!(excelExt === 'xls' || excelExt === 'xlsx')) {
            NotificationManager.error(langJson?.invalidExcelFileType || "Invalid Excel file type. Only .xls or .xlsx allowed.", langJson?.invalidFile || "Invalid File");
            setLoadingImport(false);
            return;
        }
        let zipExt = null;
        if (imageZip) {
            zipExt = imageZip.name.split('.').pop().toLowerCase();
            if (zipExt !== 'zip') {
                NotificationManager.error(langJson?.invalidImagesFileType || "Invalid images file type. Only .zip allowed.", langJson?.invalidFile || "Invalid File");
                setLoadingImport(false);
                return;
            }
        }
        const formData = new FormData();
        formData.append("excel", csvFile);
        if (imageZip) formData.append("images", imageZip);
        formData.append("mapping", JSON.stringify(mapping));
        try {
            const res = await fetch(`${API_BASE_URL}products/import`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
                body: formData,
            });
            const data = await res.json();
            if (data.preview && data.res) {
                setPreviewData(data.preview);
                setStep(4);
                NotificationManager.success(langJson?.importCompleted || "Import completed successfully.", langJson?.success || "Success");
            } else {
                NotificationManager.error(data.msg || langJson?.importFailed || "Import failed", langJson?.error || "Error");
            }
        } catch {
            NotificationManager.error(langJson?.importFailed || "Import failed", langJson?.error || "Error");
        } finally {
            setLoadingImport(false);
        }
    };

    // Step 4: Confirm import
    const handleConfirm = async () => {
        // Confirm import (pseudo API)
        try {
            const res = await fetch(`${API_BASE_URL}vendor/confirm-import`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
                body: JSON.stringify({ confirm: true }),
            });
            const data = await res.json();
            if (data.success) {
                setStep(5);
            } else {
                NotificationManager.error(langJson?.finalImportFailed || "Final import failed", langJson?.error || "Error");
            }
        } catch {
            NotificationManager.error(langJson?.finalImportFailed || "Final import failed", langJson?.error || "Error");
        }
    };

    // Download Demo Excel with Authorization header
    const handleDownloadDemoExcel = async () => {
        setLoadingDemo(true);
        try {
            const res = await fetch(`${API_BASE_URL}products/demo-excel`, {
                headers: {
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });
            if (!res.ok) throw new Error(langJson?.couldNotDownloadDemoExcel || "Failed to download");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = "demo-products.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            NotificationManager.success("Demo Excel downloaded.", "Success");
            NotificationManager.success(langJson?.demoExcelDownloaded || "Demo Excel downloaded.", langJson?.success || "Success");
        } catch {
            NotificationManager.error(langJson?.couldNotDownloadDemoExcel || "Could not download demo Excel", langJson?.error || "Error");
        } finally {
            setLoadingDemo(false);
        }
    };

    return (
        <div>
            <div className="dashHeader">
                <VendorHeader />
            </div>
            <div className="adminDash">
                <Sidebar />
                <div className="rightSide">
                    <h2>{langJson?.importProducts || "Import Products"}</h2>

                    {/* Notifications handled by NotificationManager */}

                    <div className="cardBox">
                        {step === 1 && (
                            <div className="cardBody">
                                <div className="cardTitle">
                                    <h3>{langJson?.step1UploadExcelFile || "Step 1: Upload Excel File"}</h3>
                                    <button
                                        type="button"
                                        onClick={handleDownloadDemoExcel}
                                        className="downloadBtn"
                                        disabled={loadingDemo}
                                    >
                                        {langJson?.downloadDemoExcel || "Download Demo Excel"}
                                    </button>
                                </div>
                                <label htmlFor="excel-upload-step1" style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>
                                    {langJson?.excelFileXlsXlsx || "Excel File (.xls, .xlsx)"}
                                </label>
                                <input id="excel-upload-step1" type="file" accept=".xls,.xlsx" onChange={handleSampleFile} className="inputFile" />
                                <div style={{ fontSize: "0.95rem", color: "#555", marginBottom: 12 }}>
                                    {langJson?.uploadExcelInstruction || "Please upload your product data in Excel format. Download the demo file for the required structure. Only Excel files are supported."}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="cardBody">
                                <div className="cardTitle">
                                    <h3>{langJson?.step2MapExcelColumns || "Step 2: Map Excel Columns"}</h3>
                                </div>
                                <table className="styledTable" style={{ width: "100%", marginBottom: 24 }}>
                                    <thead>
                                        <tr style={{ background: "#f7f7f7" }}>
                                            <th style={{ padding: "8px 12px" }}>{langJson?.systemField || "System Field"}</th>
                                            <th style={{ padding: "8px 12px" }}>{langJson?.excelColumn || "Excel Column"}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {SYSTEM_FIELDS.map((field) => (
                                            <tr key={field.key}>
                                                <td style={{ padding: "8px 12px" }}>{field.label}</td>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <select
                                                        value={mapping[field.key] || ""}
                                                        onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                                        className="mainSelect"
                                                    >
                                                        <option value="">{langJson?.select || "-- Select --"}</option>
                                                        {csvColumns.map((col) => (
                                                            <option key={col} value={col}>{col}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={saveProfile} className="mainBtn" disabled={loadingMapping}>
                                    {langJson?.saveMappingProfile || "Save Mapping Profile"}
                                </button>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="cardBody">
                                <div className="cardTitle">
                                    <h3>{langJson?.step3UploadImagesZip || "Step 3: Upload Images ZIP"}</h3>
                                </div>
                                <label htmlFor="images-upload" style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>
                                    {langJson?.imagesZipFile || "Images ZIP File (.zip)"}
                                </label>
                                <input id="images-upload" type="file" accept=".zip" onChange={handleImageZip} className="inputFile" />
                                <div style={{ fontSize: "0.95rem", color: "#555", marginBottom: 12 }}>
                                    {langJson?.uploadImagesInstruction || "Upload a ZIP file containing product images. Image filenames should match the product identifiers in your Excel file for correct mapping."}
                                </div>
                                <button onClick={handleImport} className="mainBtn" style={{ marginTop: 16 }} disabled={loadingImport}>
                                    {langJson?.import || "Import"}
                                </button>
                            </div>
                        )}

                        {step === 4 && previewData && (
                            <div className="cardBody">
                                <div className="cardTitle">
                                    <h3>{langJson?.step4ImportResults || "Step 4: Import Results"}</h3>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <strong>{langJson?.processedRows || "Processed Rows:"}</strong> {previewData.processed_rows}
                                    <span style={{ marginLeft: 24 }}><strong>{langJson?.failedRows || "Failed Rows:"}</strong> {previewData.failed_rows}</span>
                                    <span style={{ marginLeft: 24 }}><strong>{langJson?.productsCreated || "Products Created:"}</strong> {previewData.products_created}</span>
                                    <span style={{ marginLeft: 24 }}><strong>{langJson?.productsUpdated || "Products Updated:"}</strong> {previewData.products_updated}</span>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <strong>{langJson?.importedItems || "Imported Items:"}</strong>
                                    <ul className="importedList">
                                        {previewData.imported && previewData.imported.length > 0 ? (
                                            previewData.imported.map(item => (
                                                <li key={item.id || item.title} className="importedItem">{item.title || String(item)}</li>
                                            ))
                                        ) : (
                                            <li className="importedNone">{langJson?.noItemsImported || "No items imported."}</li>
                                        )}
                                    </ul>
                                </div>
                                {previewData.errors && previewData.errors.length > 0 && (
                                    <div className="errorMsg errorListBox">
                                        <strong style={{ display: 'block', marginBottom: 8 }}>{langJson?.errors || "Errors:"}</strong>
                                        <ul className="errorList">
                                            {previewData.errors.map((err, idx) => {
                                                if (typeof err === 'object' && err !== null) {
                                                    // Defensive: stringify if not row/error keys
                                                    if ('row' in err && 'error' in err) {
                                                        return <li key={idx} className="errorItem"><span className="errorRow">{langJson?.row || "Row"} {err.row}:</span> <span className="errorText">{String(err.error)}</span></li>;
                                                    } else {
                                                        return <li key={idx} className="errorItem">{JSON.stringify(err)}</li>;
                                                    }
                                                }
                                                return <li key={idx} className="errorItem">{String(err)}</li>;
                                            })}
                                        </ul>
                                    </div>
                                )}
                                <button className="mainBtn" style={{ marginTop: 24 }} onClick={() => window.location.href = '/products'}>
                                    {langJson?.goToProducts || "Go to Products"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <VendorFooter />
        </div>
    );

}
export default VendorProductImport;


